import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import { authApi } from '../api/auth.api';

const STORAGE_KEY = 'twinports-auth';
const REMEMBER_KEY = 'twinports-remember';

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

// "Remember me" keeps the session in localStorage; otherwise it lives in sessionStorage and ends with the tab.
const isRemembered = () => safe(() => localStorage.getItem(REMEMBER_KEY) === 'true', false);

const sessionAwareStorage: StateStorage = {
  getItem: (name) => safe(() => sessionStorage.getItem(name) ?? localStorage.getItem(name), null),
  setItem: (name, value) =>
    safe(() => {
      const [target, other] = isRemembered() ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
      target.setItem(name, value);
      other.removeItem(name);
    }, undefined),
  removeItem: (name) =>
    safe(() => {
      localStorage.removeItem(name);
      sessionStorage.removeItem(name);
    }, undefined),
};

const decodeJwt = (token: string): Record<string, unknown> => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return {};
  }
};

const userFromToken = (token: string) => {
  const payload = decodeJwt(token);
  const str = (key: string) => (typeof payload[key] === 'string' ? (payload[key] as string) : undefined);
  return {
    email: str('email'),
    username: str('name') ?? str('preferred_username') ?? str('email'),
  };
};

interface AuthStore {
  authToken?: string;
  refreshToken?: string;
  email?: string;
  username?: string;
  isAuthenticated: boolean;
  login: (email: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (authToken: string, refreshToken: string) => void;
  clearTokens: () => void;
}

const signedOut = {
  authToken: undefined,
  refreshToken: undefined,
  email: undefined,
  username: undefined,
  isAuthenticated: false,
};

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...signedOut,
      login: async (email, password, remember) => {
        const response = await authApi.login(email, password);
        safe(() => localStorage.setItem(REMEMBER_KEY, String(remember)), undefined);
        set({
          ...userFromToken(response.accessToken),
          authToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
        });
      },
      logout: async () => {
        const { refreshToken } = get();
        set(signedOut);
        if (refreshToken) await authApi.logout(refreshToken);
      },
      setTokens: (authToken, refreshToken) =>
        set({ ...userFromToken(authToken), authToken, refreshToken, isAuthenticated: true }),
      clearTokens: () => set(signedOut),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionAwareStorage),
      partialize: (s) => ({ authToken: s.authToken, refreshToken: s.refreshToken }),
      merge: (persisted, current) => {
        const { authToken, refreshToken } = (persisted ?? {}) as Partial<AuthStore>;
        if (!authToken) return current;
        return { ...current, ...userFromToken(authToken), authToken, refreshToken, isAuthenticated: true };
      },
    },
  ),
);

export default useAuthStore;
